import {
    type RefObject,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import type { VideoTypes } from "@bloomberg/media-ui-av";

import { DEFAULT_VISIBILITY_THRESHOLD } from "~components/shared/video-player/VideoPlayerContext";
import { useIntersectionObserverTracker } from "~utils/useIntersectionObserverTracker";

declare type ViewportState = "enter" | "leave";
export default function useVideoViewportAutoplay(
    playerRef: RefObject<
        VideoTypes.MediaPlayer | VideoTypes.PlaylistMediaPlayer
    >
) {
    const [userStarted, setUserStarted] = useState(false);
    const [userPaused, setUserPaused] = useState(false);
    const [viewState, setViewState] = useState<ViewportState>();

    const intersectionObserverTracker = useIntersectionObserverTracker(
        () => setViewState("enter"),
        () => setViewState("leave"),
        DEFAULT_VISIBILITY_THRESHOLD
    );

    useEffect(() => {
        if (!playerRef?.current) return;

        if (viewState === "enter") {
            if (userStarted && !userPaused) {
                playerRef.current.play();
            }
            return;
        }

        if (viewState === "leave") {
            const isPlaying =
                "isPlaying" in playerRef.current
                    ? playerRef.current.isPlaying()
                    : true;

            if (isPlaying) {
                playerRef.current.pause();
            }
        }
    }, [playerRef, viewState, userStarted, userPaused]);

    const onReady = useCallback(() => {
        if (playerRef.current) {
            playerRef.current.on("willPlay", () => setUserStarted(true));
            playerRef.current.on("click", () => {
                const isPlaying =
                    playerRef.current && "isPlaying" in playerRef.current
                        ? playerRef.current.isPlaying()
                        : true;

                setUserPaused(!isPlaying);
            });
        }
    }, [playerRef]);

    return useMemo(
        () => ({
            onReady,
            intersectionObserverTracker,
        }),
        [intersectionObserverTracker, onReady]
    );
}
